
import React, { useEffect } from 'react';
import ReactGA from 'react-ga4';

import { useDispatch, useSelector } from 'react-redux';

import { ResizableBox } from 'react-resizable';

import { getDataAsync } from './store/data/data.slice';
import { selectIsLoading } from './store/data/data.selector';
import { selectCurrentLocation, selectUseStationData } from './store/map/map.selector';
import { selectIsClimate } from './store/options/options.selector';
import {
  selectIsChartVisible,
  selectIsEpisodeTimelineVisible,
  selectShowRecentUpdates,
  selectSmallScreen,
} from './store/ui/ui.selector';

import { useCurrentWidth, useCurrentHeight } from './utilities/windowSize';

import Header from './components/header/header.component';
import Footer from './components/footer/footer.component';
import OptionsPanel from './components/options-panel/options-panel.component';
import Loading from './components/loading/loading.component';
import Map from './components/map/map.component';
import Legend from './components/legend/legend.component';
import LocationSearch from './components/location-search/location-search.component';
import GettingStarted from './components/getting-started/getting-started.component';
import HelpDrawer from './components/help-drawer/help-drawer.components';
import InfoModal from './components/info-modal/info-modal.component';
import OverviewCard from './components/overview-card/overview-card.component';
import ChartContainer from './components/chart-container/chart-container.component';
import EpisodesTimeline from './components/episodes-timeline/episodes-timeline.component';
import VersionBanner from './components/version-banner/version-banner.component';
import RecentUpdates from './components/recent-updates/recent-updates.component';

import './App.scss';
import EventDisplay from './components/event-display/event-display.component';
import { changeSmallScreen } from './store/ui/ui.slice';
import SmallScreen from './components/small-screen/small-screen.component';

window.mobileCheck = function () {
  let check = false;
  (function (a) {
    if (
      // eslint-disable-next-line no-useless-escape
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
        a
      ) ||
      // eslint-disable-next-line no-useless-escape
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
        a.substr(0, 4)
      )
    )
      check = true;
  })(navigator.userAgent || navigator.vendor || window.opera);
  return check;
};

function App() {
  const dispatch = useDispatch();
  const isLoading = useSelector(selectIsLoading);
  const currentLocation = useSelector(selectCurrentLocation);
  const useStation = useSelector(selectUseStationData);
  const isChartVisible = useSelector(selectIsChartVisible);
  const isEpisodeTimelineVisible = useSelector(selectIsEpisodeTimelineVisible);
  const isClimate = useSelector(selectIsClimate);
  const { isSmall, bypass, reason } = useSelector(selectSmallScreen);
  const showRecentUpdates = useSelector(selectShowRecentUpdates);

  let width = useCurrentWidth();
  let height = useCurrentHeight();
  const resizeableHeight = Math.max(height - 306, 309);
  const resizableWidth = Math.max((width - 156) / 4 + 21, 232);

  useEffect(() => {
    dispatch(getDataAsync());
  }, [dispatch]);

  // Send analytics data to Google
  useEffect(() => {
    ReactGA.initialize('G-X49KHJYHCX');
    ReactGA.send({ hitType: 'pageview', page: window.location.pathname + window.location.search });
  }, []);

  useEffect(() => {
    if (!bypass) {
      let newReason = '';
      let newIsSmall = true;
      if (window.mobileCheck()) {
        newReason = 'size';
      } else if (width < 1000 && height < 650) {
        newReason = 'width and height';
      } else if (width < 1000) {
        newReason = 'width';
      } else if (height < 650) {
        newReason = 'height';
      } else {
        newIsSmall = false;
      }

      if (reason !== newReason || isSmall !== newIsSmall) {
        dispatch(
          changeSmallScreen({ isSmall: newIsSmall, reason: newReason, bypass })
        );
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width, height, dispatch]);



  console.log('Testing dev deploy');

  return (
    <div id='app-container'>
      <Header />
      <main id='app-body'>
        {isLoading && <Loading />}
        <Map />
        <Legend />
        <OptionsPanel />
        {(currentLocation.name || useStation) ? <OverviewCard /> : <GettingStarted />}
        <ResizableBox
          id='slider'
          className='fadeable'
          style={{
            pointerEvents:
              !isClimate && isChartVisible && isEpisodeTimelineVisible
                ? 'all'
                : 'none',
            opacity:
              !isClimate && isChartVisible && isEpisodeTimelineVisible ? 1 : 0,
          }}
          width={resizableWidth}
          height={resizeableHeight}
          resizeHandles={['e']}
          axis={'x'}
          minConstraints={[resizableWidth, resizeableHeight]}
          maxConstraints={[width - 70, resizeableHeight]}
        >
          <EpisodesTimeline />
        </ResizableBox>
        <ChartContainer />
        <EventDisplay />
        <LocationSearch />
        <HelpDrawer />
        <InfoModal />
        <VersionBanner />
      </main>
      <Footer />

      {showRecentUpdates && <RecentUpdates />}
      {!bypass && isSmall && <SmallScreen />}
    </div>
  );
}

export default App;
